// useAddressSearchPreview.js
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { getLoanBalanceForSellerNetSheet } from "../../../utils/calculator";
import { shouldEstimateLoanBalance } from "../../../utils/autoEstimateLoan";
import { useGooglePlaces } from "./useGooglePlaces";
import { fetchCityStateForZip, fetchStreetViewCamera } from "../utils/apiUtils";

/**
 * mapTtb - Process raw TTB data into structured format
 * @param {Object} raw - API response data
 * @param {string} mode - Calculator mode (seller/buyer)
 * @param {string} siteUnitUpdated - Site unit value
 * @returns {Object} Structured TTB data
 */
function mapTtb(raw, mode, siteUnitUpdated) {
  const cs = raw?.data || {};
  const ttb = cs?.ttb || {};

  return {
    apn: ttb.apn || "NA",
    mode: mode || "",
    ownername: ttb.ownername || "NA",
    type: ttb.type || "NA",
    bed: ttb.bed || "NA",
    bath: ttb.bath || "NA",
    sqft: ttb.sqft || "NA",
    built: ttb.built || "NA",
    county_name: cs.county_name || "",
    state_name: cs.state_name || "",
    state_code: cs.state_code || "",
    stateid: cs.stateid || "",
    countyid: cs.countyid || "",
    assessedValue: raw?.assessedValue ?? ttb.assessedValue ?? "",
    caTaxRate: raw?.caTaxRate ?? ttb.caTaxRate ?? "",
    adjustedCashPercent: raw?.caTaxRate ?? ttb.adjCashPer ?? "",
    inflationRate: raw?.caTaxRate ?? ttb.infiRate ?? "",
    taxAmount: raw?.taxAmount ?? ttb.taxAmount ?? "",
    taxYear: raw?.taxYear ?? ttb.taxYear ?? "",
    site_unit: siteUnitUpdated || (raw?.site_unit ?? ttb.site_unit ?? ""),
  };
}


/**
 * processEnrichmentResult - Process raw API response into enrichment result (reserved for future use)
 * @param {Object} raw - Raw API response
 * @param {string} mode - Calculator mode (seller/buyer)
 * @param {string} siteUnit - Site unit value
 * @returns {Object} Enrichment result
 */
// eslint-disable-next-line no-unused-vars
function processEnrichmentResult(raw, mode, siteUnit) {
  const cs = raw?.data || {};
  const ttbRaw = cs?.ttb || {};
  const ttbStatus = ttbRaw?.status !== undefined ? Number(ttbRaw.status) : undefined;

  const success = raw?.status === "success" && ttbStatus !== 0;

  if (!success) {
    return {
      success: false,
      ttbData: null,
      error: "Property not found or not supported",
      raw,
    };
  }

  const siteUnitUpdated = siteUnit || "";
  let resolvedTtb = mapTtb(raw, mode || "", siteUnitUpdated);

  if (shouldEstimateLoanBalance(mode)) {
    try {
      const loanBalance = getLoanBalanceForSellerNetSheet(
        ttbRaw?.loanAmount,
        cs?.loanRate || 0,
        30,
        ttbRaw?.loanDate
      );
      if (loanBalance) resolvedTtb.loanBalance = loanBalance;
    } catch {}
  }

  return {
    success: true,
    ttbData: resolvedTtb,
    error: null,
    raw,
  };
}

const initialState = (initialHeader) => ({
  query: "",
  header: initialHeader || "",
  suggestions: [],
  activeIndex: -1,
  isLoadingEnrichment: false,
  showPreview: false,
  propertyNotFound: false,
  ttbData: {},
  showSiteUnit: false,
  siteUnit: "",
});

const patchReducer = (s, a) => ({ ...s, ...a });

function parsePlace(place, fallbackDescription, siteUnit, mode) {
  const comps = place?.address_components || [];
  const formatted = place?.formatted_address || fallbackDescription || "";

  const findLong = (type) =>
    comps.find((c) => c.types?.includes(type))?.long_name || "";
  const findShort = (type) =>
    comps.find((c) => c.types?.includes(type))?.short_name || "";

  const payload = {
    zip: findLong("postal_code") || "",
    street_no: findLong("street_number") || "",
    route: findShort("route") || findLong("route") || "",
    route_long: findLong("route") || findShort("route") || "",
    site_unit: siteUnit || findLong("subpremise") || "",
    city:
      findLong("locality") ||
      findLong("sublocality") ||
      findLong("neighborhood") ||
      "",
    state_code: findShort("administrative_area_level_1") || "",
    state_name: findLong("administrative_area_level_1") || "",
    formatted_address: formatted || "",
    calc: mode || "seller",
  };

  const loc = place?.geometry?.location;
  const latlng =
    loc && typeof loc.lat === "function"
      ? { lat: loc.lat(), lng: loc.lng() }
      : loc && typeof loc.lat === "number"
      ? { lat: loc.lat, lng: loc.lng }
      : null;

  return { formatted, payload, latlng };
}


export function useAddressSearchPreview({
  mode = "seller",
  initialHeader = "",
  onEnriched,
  onAddressEdited,
  onAddressSelected,
  onStreetViewChanged,
}) {
  const [selectedPlace , setSelectedPlace] = useState({});
  const [state, dispatch] = useReducer(
    patchReducer,
    initialState(initialHeader)
  );
  const patch = useCallback((p) => dispatch(p), []);

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const places = useGooglePlaces({ apiKey });

  // stable external callbacks
  const onEnrichedRef = useRef(onEnriched);
  const onAddressEditedRef = useRef(onAddressEdited);
  const onAddressSelectedRef = useRef(onAddressSelected);
  const onStreetViewChangedRef = useRef(onStreetViewChanged);
  useEffect(() => void (onEnrichedRef.current = onEnriched), [onEnriched]);
  useEffect(
    () => void (onAddressEditedRef.current = onAddressEdited),
    [onAddressEdited]
  );
  useEffect(
    () => void (onAddressSelectedRef.current = onAddressSelected),
    [onAddressSelected]
  );
  useEffect(
    () => void (onStreetViewChangedRef.current = onStreetViewChanged),
    [onStreetViewChanged]
  );

  // sync header
  useEffect(() => {
    patch({ header: initialHeader || "" });
  }, [initialHeader, patch]);

  // dropdown suppression: after selecting, do NOT fetch predictions until user types again
  const suppressPredictionsRef = useRef(false);

  // Function to suppress predictions programmatically
  const suppressPredictions = useCallback(() => {
    suppressPredictionsRef.current = true;
  }, []);

  // UI refs
  const suggestionItemRefs = useRef([]);

  // Map refs (DOM)
  const panoramaContainerRef = useRef(null);
  const satelliteContainerRef = useRef(null);

  // Map instances
  const svPanoramaRef = useRef(null);
  const fallbackMapRef = useRef(null);
  const satelliteMapRef = useRef(null);
  const satelliteMarkerRef = useRef(null);
  const fallbackMarkerRef = useRef(null);

  const selectedLatLngRef = useRef(null);
  const streetViewAbortRef = useRef(null);
  const streetViewChangeTimerRef = useRef(null);

  const resetMaps = useCallback(() => {
    svPanoramaRef.current = null;
    fallbackMapRef.current = null;
    satelliteMapRef.current = null;
    satelliteMarkerRef.current = null;
    fallbackMarkerRef.current = null;

    if (streetViewChangeTimerRef.current) {
      clearTimeout(streetViewChangeTimerRef.current);
      streetViewChangeTimerRef.current = null;
    }

    if (panoramaContainerRef.current)
      panoramaContainerRef.current.innerHTML = "";
    if (satelliteContainerRef.current)
      satelliteContainerRef.current.innerHTML = "";
  }, []);

  useEffect(() => resetMaps, [resetMaps]);

  const setupMaps = useCallback(
    async (latlng, address) => {
      if (!latlng) return;
      await places.ensureReady();

      const center = { lat: latlng.lat, lng: latlng.lng };

      // Satellite (reuse if possible)
      if (satelliteContainerRef.current) {
        if (!satelliteMapRef.current) {
          satelliteMapRef.current = new window.google.maps.Map(
            satelliteContainerRef.current,
            {
              center,
              zoom: 19,
              mapTypeId: window.google.maps.MapTypeId.SATELLITE,
            }
          );
          satelliteMarkerRef.current = new window.google.maps.Marker({
            position: center,
            map: satelliteMapRef.current,
          });
        } else {
          satelliteMapRef.current.setCenter(center);
          satelliteMarkerRef.current?.setPosition(center);
        }
      }

      // StreetView: ask the backend for the exact camera (panoId/heading/zoom) it also
      // uses for the PDF, instead of picking a panorama in the browser. Calling
      // StreetViewService.getPanorama or setting `position` here would let Google choose
      // a different nearby panorama than the one the PDF shows.
      try {
        streetViewAbortRef.current?.abort?.();
      } catch {}
      const svController = new AbortController();
      streetViewAbortRef.current = svController;

      let cam = { available: false };
      if (address) {
        try {
          cam = await fetchStreetViewCamera(address, {
            controller: svController,
          });
        } catch (e) {
          if (e?.name === "CanceledError" || e?.name === "AbortError") return;
          cam = { available: false };
        }
      }

      if (svController.signal.aborted || !panoramaContainerRef.current) return;
      panoramaContainerRef.current.innerHTML = "";

      if (cam?.available && cam.panoId) {
        svPanoramaRef.current = new window.google.maps.StreetViewPanorama(
          panoramaContainerRef.current,
          {
            pano: cam.panoId,
            pov: { heading: cam.heading ?? 0, pitch: cam.pitch ?? 0 },
            zoom: cam.zoom ?? 1.17,
          }
        );
        svPanoramaRef.current.setVisible(true);

        // Surface whatever camera is currently showing (debounced, since pov_changed fires
        // continuously while dragging) so a caller — e.g. the Showcase PDF — can render the
        // exact view the user ended up with instead of always recomputing the default.
        const notifyStreetViewChanged = () => {
          if (streetViewChangeTimerRef.current) clearTimeout(streetViewChangeTimerRef.current);
          streetViewChangeTimerRef.current = setTimeout(() => {
            const pano = svPanoramaRef.current;
            if (!pano || !onStreetViewChangedRef.current) return;
            const pov = pano.getPov();
            onStreetViewChangedRef.current({
              panoId: pano.getPano(),
              heading: pov?.heading ?? cam.heading ?? 0,
              pitch: pov?.pitch ?? cam.pitch ?? 0,
              zoom: pano.getZoom(),
            });
          }, 400);
        };
        svPanoramaRef.current.addListener("pov_changed", notifyStreetViewChanged);
        svPanoramaRef.current.addListener("zoom_changed", notifyStreetViewChanged);
        svPanoramaRef.current.addListener("pano_changed", notifyStreetViewChanged);
        return;
      }

      panoramaContainerRef.current.innerHTML =
        '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#6b7280;font-size:13px;text-align:center;padding:8px;">Street View not available</div>';
    },
    [places]
  );

  // scroll active suggestion into view
  useEffect(() => {
    const el = suggestionItemRefs.current[state.activeIndex];
    el?.scrollIntoView?.({ block: "nearest" });
  }, [state.activeIndex]);

  // request guards
  const predictionRidRef = useRef(0);
  const selectRidRef = useRef(0);

  // predictions effect (debounced)
  useEffect(() => {
    if (!apiKey) return;

    // IMPORTANT: keep dropdown closed after selection until user types again
    if (suppressPredictionsRef.current) {
      if (state.suggestions.length || state.activeIndex !== -1) {
        patch({ suggestions: [], activeIndex: -1 });
      }
      return;
    }

    const q = (state.query || "").trim();

    if (q.length < 2) {
      if (state.suggestions.length || state.activeIndex !== -1) {
        patch({ suggestions: [], activeIndex: -1 });
      }
      return;
    }

    const rid = ++predictionRidRef.current;

    const t = setTimeout(async () => {
      try {
        const preds = await places.getPredictions({
          input: q,
          limit: 5,
          country: "us",
        });
        if (rid !== predictionRidRef.current) return;
        patch({ suggestions: preds, activeIndex: -1 });
      } catch {
        if (rid === predictionRidRef.current)
          patch({ suggestions: [], activeIndex: -1 });
      }
    }, 350);

    return () => clearTimeout(t);
  }, [
    apiKey,
    places,
    patch,
    state.query,
    state.suggestions.length,
    state.activeIndex,
  ]);

  const onChange = useCallback(
    (e) => {
      // user typing -> re-enable predictions
      suppressPredictionsRef.current = false;

      const next = e.target.value;
      patch({
        query: next,
        activeIndex: -1,
        showPreview: false,
        propertyNotFound: false,
      });

      onAddressEditedRef.current?.(next);
    },
    [patch]
  );

  const handleSiteUnitCheckboxChange = useCallback(
    (checked) => {
      
      if (checked && !state.siteUnit && state.ttbData?.site_unit && state.ttbData.site_unit !== "") {
        patch({ 
          showSiteUnit: checked,
          siteUnit: state.ttbData.site_unit 
        });
      } else {
        patch({ showSiteUnit: checked });
      }
    },
    [patch, state.siteUnit, state.ttbData]
  );

    // abort previous enrichment call
    const enrichAbortRef = useRef(null);

    const setUpPlaceDetails = useCallback(async ({placeId, description , siteUnit}) =>{
      if (!apiKey || !placeId) return;

    //  console.log("setUpPlaceDetails");
    //  console.log(placeId, description, siteUnit);
  
        // IMPORTANT: after selection, keep predictions suppressed until user types
      suppressPredictionsRef.current = true;
  
      resetMaps();
      const rid = ++selectRidRef.current;
      let payloadForCallback = null;
        let raw = null;
        let resolvedTtb = null;
      try {
        const place = await places.getPlaceDetails({ placeId });
        setSelectedPlace({placeId: placeId, place: place, description: description});
        
        if (rid !== selectRidRef.current) return;
  
        const { formatted, payload, latlng } = parsePlace(place, description, siteUnit, mode);
  
        payload.mode = mode || "";
        payloadForCallback = payload;
  
        // update input + header (this won't re-open dropdown because suppression is on)
        patch({ header: formatted, query: formatted });
  
        selectedLatLngRef.current = latlng;
  
        // enrichment start
        patch({ isLoadingEnrichment: true });
  
        // cancel previous
        try {
          enrichAbortRef.current?.abort?.();
        } catch {}
        const controller = new AbortController();
        enrichAbortRef.current = controller;
  
        try {
          const result = await fetchCityStateForZip(payload, {
            controller,
            mode,
            siteUnit,
            isManual: false,
          });

          if (rid !== selectRidRef.current) return;

          // Update payload with normalized data
          Object.assign(payload, result.payload);

          if (!result.success) {
            patch({ propertyNotFound: true, ttbData: {} });
          } else {
            // Initialize site unit state
            const siteUnitValue = siteUnit || payload?.site_unit || "";

            patch({
              ttbData: result.ttbData || {},
              propertyNotFound: false,
              isLoadingEnrichment: true,
              showPreview: true,
              siteUnit: siteUnitValue && siteUnitValue !== "" ? siteUnitValue : "",
            });

            resolvedTtb = result.ttbData;
          }
        } catch (e) {
          if (rid !== selectRidRef.current) return;
  
          // aborted -> do nothing
          if (e?.name === "CanceledError" || e?.name === "AbortError") return;
  
          patch({ propertyNotFound: true, ttbData: {} });
        } finally {
          if (rid !== selectRidRef.current) return;
  
          patch({ isLoadingEnrichment: false, showPreview: true });
  
          // init maps after preview mounts
          setTimeout(() => {
            if (rid !== selectRidRef.current) return;
            setupMaps(selectedLatLngRef.current, payload.formatted_address || formatted);
          }, 0);
  
          if (payloadForCallback) {
            payloadForCallback.enrichment = {
              ...(raw?.data || {}),
              mode: mode || "",
            };
  
            onEnrichedRef.current?.({
              payload: payloadForCallback,
              ttb: resolvedTtb || null,
              raw,
              showPreview: true,
              showSiteUnit: state.showSiteUnit,
            });
          }
        }
      } catch {
        if (rid !== selectRidRef.current) return;
        patch({
          isLoadingEnrichment: false,
          propertyNotFound: true,
          ttbData: {},
          showPreview: true,
        });
      }
    }, [apiKey, mode, patch, places, resetMaps, setupMaps, state.showSiteUnit]);

  const handleSiteUnitInputChange = useCallback(
    async (value) => {
      patch({ siteUnit: value });      
      await setUpPlaceDetails({placeId: selectedPlace.placeId, description: selectedPlace.description , siteUnit: value});

    },
    [patch, selectedPlace.description, selectedPlace.placeId, setUpPlaceDetails]
  );

  const blurTimerRef = useRef(null);
  const onBlur = useCallback(() => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => patch({ suggestions: [] }), 150);
  }, [patch]);

  const setActiveIndex = useCallback(
    (idx) => patch({ activeIndex: idx }),
    [patch]
  );



  const selectSuggestion = useCallback(
    async (item) => {
    //  console.log('🔍 [useAddressSearchPreview] Suggestion selected:', item);
      const placeId = item?.place_id;
      const description = item?.description || "";
    //  console.log('📍 [useAddressSearchPreview] Place ID:', placeId, 'Description:', description);

      patch({
        query: description,
        suggestions: [],
        activeIndex: -1,
        showPreview: false,
        propertyNotFound: false,
        ttbData: {},
      });
   //   console.log('🔄 [useAddressSearchPreview] State reset for new selection');

      // Get place details and call address selected callback with parsed data
      if (placeId) {
        try {
      //    console.log('📡 [useAddressSearchPreview] Fetching place details...');
          const place = await places.getPlaceDetails({ placeId });
      //    console.log('📡 [useAddressSearchPreview] Place details received:', place);

          const { formatted, payload } = parsePlace(place, description, undefined, mode);
      //    console.log('📝 [useAddressSearchPreview] Parsed place data:', { formatted, payload, latlng });

          // Call the address selected callback with the parsed address data
          const addressData = {
            street_no: payload.street_no,
            street_name: payload.route,
            city: payload.city,
            state: payload.state_code || payload.state_name,
            zip: payload.zip,
            unitNumber: payload.site_unit,
            formattedAddress: formatted
          };
       //   console.log('📤 [useAddressSearchPreview] Calling onAddressSelected with:', addressData);

          onAddressSelectedRef.current?.(addressData);
      //    console.log('✅ [useAddressSearchPreview] Address selected callback completed');
        } catch (error) {
      //    console.error("❌ [useAddressSearchPreview] Error getting place details:", error);
        }
      }

      await setUpPlaceDetails({placeId, description});
    //  console.log('🏁 [useAddressSearchPreview] Suggestion selection completed');

    },
    [patch, setUpPlaceDetails, places, mode]
  );

  const onKeyDown = useCallback(
    (e) => {
      const { suggestions, activeIndex } = state;
      if (!suggestions.length) return;

      const max = suggestions.length - 1;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        patch({ activeIndex: activeIndex < max ? activeIndex + 1 : max });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        patch({ activeIndex: activeIndex > 0 ? activeIndex - 1 : -1 });
      } else if (e.key === "Escape") {
        patch({ suggestions: [], activeIndex: -1 });
      } else if (e.key === "Enter") {
        if (activeIndex >= 0 && activeIndex <= max) {
          e.preventDefault();
          selectSuggestion(suggestions[activeIndex]);
        }
      }
    },
    [patch, selectSuggestion, state]
  );

  const clearPropertyNotFound = useCallback(() => {
    patch({ propertyNotFound: false });
  }, [patch]);

  /**
   * Apply result from manual address search (get-city-state-for-zip) - same logic as
   * autocomplete enrichment so PropertyPreview and onEnriched stay in sync.
   * @param {Object|null} rawResponse - API response data; null = property not found
   * @param {Object} requestPayload - Request params sent to API (street_number, street_name, city, state, zip, etc.)
   */
  const applyManualAddressEnrichment = useCallback(
    (enrichmentResult, requestPayload = {}, options = {}) => {
      if (!enrichmentResult) {
        patch({ propertyNotFound: true, ttbData: {}, showPreview: true });
        return;
      }

      // Check if this is a "fail" response but address data is valid
      const isAddressValidButNoPropertyDetails =
        enrichmentResult.raw?.status === "fail" && enrichmentResult.raw?.data?.city;

      if (!enrichmentResult.success && !isAddressValidButNoPropertyDetails) {
        patch({ propertyNotFound: true, ttbData: {}, showPreview: true });
        return;
      }

      const siteUnitValue = requestPayload.site_unit || requestPayload.unit_number || enrichmentResult.raw?.site_unit || "";

      patch({
        ttbData: enrichmentResult.ttbData || {},
        propertyNotFound: false, // Show property section even for fail responses with valid address
        showPreview: true,
        isLoadingEnrichment: false,
        siteUnit: siteUnitValue && siteUnitValue !== "" ? siteUnitValue : "",
        header: requestPayload.street_no+" "+requestPayload.route_long+" "+requestPayload.city+" "+requestPayload.state_code+" "+requestPayload.zip,
      });

      // Geocode the address to get coordinates for Google Maps. Same format as the
      // addressMeta.address sent to the PDF (see AddressSearchPreview.js), so the
      // Street View camera request below hits the backend's cache for this address.
      const fullAddress =
        requestPayload.street_no && requestPayload.route_long
          ? `${requestPayload.street_no} ${requestPayload.route_long}, ${requestPayload.city}, ${requestPayload.state_code} ${requestPayload.zip}`
          : [
              requestPayload.street_no,
              requestPayload.route_long,
              requestPayload.city,
              requestPayload.state_code,
              requestPayload.zip,
            ]
              .filter(Boolean)
              .join(", ");

      if (fullAddress && window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: fullAddress }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results?.[0]?.geometry?.location) {
            const location = results[0].geometry.location;
            selectedLatLngRef.current = {
              lat: location.lat(),
              lng: location.lng()
            };
            // Setup Google Maps views
            setTimeout(() => {
              setupMaps(selectedLatLngRef.current, fullAddress);
            }, 0);
          }
        });
      }

      // Call enrichment callback for successful responses or valid addresses
      if (enrichmentResult.success) {
        // Use the payload from the enrichment result
        const payloadForCallback = {
          ...enrichmentResult.payload,
          site_unit: siteUnitValue,
          mode: mode || "",
        };

        onEnrichedRef.current?.({
          payload: payloadForCallback,
          ttb: enrichmentResult.ttbData,
          raw: enrichmentResult.raw,
          showPreview: true,
          showSiteUnit: state.showSiteUnit,
          fromParcelSelection: options.fromParcelSelection || false,
        });
      }
    },
    [mode, patch, state.showSiteUnit, setupMaps]
  );

  return useMemo(
    () => ({
      ...state,

      onChange,
      onBlur,
      onKeyDown,
      selectSuggestion,
      setActiveIndex,
      handleSiteUnitCheckboxChange,
      handleSiteUnitInputChange,
      clearPropertyNotFound,
      applyManualAddressEnrichment,
      patch, // Expose patch function for programmatic updates
      suppressPredictions, // For suppressing autocomplete

      suggestionItemRefs,
      panoramaContainerRef,
      satelliteContainerRef,
    }),
    [state, onChange, onBlur, onKeyDown, selectSuggestion, setActiveIndex, handleSiteUnitCheckboxChange, handleSiteUnitInputChange, clearPropertyNotFound, applyManualAddressEnrichment, patch, suppressPredictions]
  );
}
